import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { FreeUsagePeriod } from '@/entities/subscription';

type Props = {
  periods: FreeUsagePeriod[];
};

const formatPeriodLabel = (isoDate: string, locale: string) => {
  // isoDate is a YYYY-MM-DD string; parse as a local date rather than UTC so
  // the month label matches the billing period regardless of TZ offset.
  const [yearStr, monthStr, dayStr] = isoDate.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;
  const day = Number(dayStr);
  const date = new Date(year, month, day);
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
};

const formatAmount = (amountBilledCents: number, locale: string) => {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(amountBilledCents / 100);
};

export const UsageHistoryTable = ({ periods }: Props) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('subscription.usage.columnPeriod')}</TableCell>
            <TableCell align="right">{t('subscription.usage.columnRepairOrders')}</TableCell>
            <TableCell align="right">{t('subscription.usage.columnAmount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {periods.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                {t('subscription.usage.noHistory')}
              </TableCell>
            </TableRow>
          )}
          {periods.map((period) => (
            <TableRow key={`${period.periodStart}-${period.periodEnd}`}>
              <TableCell>{formatPeriodLabel(period.periodStart, locale)}</TableCell>
              <TableCell align="right">{period.roCount}</TableCell>
              <TableCell align="right">{formatAmount(period.amountBilledCents, locale)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
